import * as amplitude from '@amplitude/analytics-browser';
import dayjs from 'dayjs';
import mixpanel from 'mixpanel-browser';

import { config } from 'utils/config/env';
import { Logger } from 'utils/logger';
import {
  getErrorMessage,
  getErrorStack,
  isErrorWithMessage,
} from 'utils/type-guards';

import { useWorkspaceStore } from 'store/workspace.store';

declare global {
  interface Window {
    mixpanel?: {
      get_distinct_id: () => string;
    };
    testErrorTracking?: () => void;
    trackError?: typeof trackError;
    trackAPIError?: typeof trackAPIError;
  }
}

/**
 * Initializes analytics services (Mixpanel and Amplitude).
 * Sets up global error listeners for automatic error tracking.
 */
export const initializeAnalytics = () => {
  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.init(config.VITE_MIXPANEL_TOKEN, {
      debug: false, // Disable debug logging to reduce console spam
      api_host: config.VITE_MIXPANEL_API_HOST,
      persistence: 'localStorage',
      autocapture: true,
      // Session replay percentage from env variable
      // https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web
      record_sessions_percent: config.VITE_MIXPANEL_RECORDING_PERCENT,
      record_mask_text_selector: '', // Unmask all text for session replay
      ignore_dnt: true,
    });
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.init(config.VITE_AMPLITUDE_API_KEY, {
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
      },
    });
  }

  window.addEventListener('error', function (event) {
    trackError({
      message: typeof event.message === 'string' ? event.message : undefined,
      error: event.error,
      context: {
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', function (event) {
    trackError({
      message: event.reason?.message || 'Unhandled promise rejection',
      error: event.reason,
    });
  });
};

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  API = 'api',
  VALIDATION = 'validation',
  RUNTIME = 'runtime',
  UI = 'ui',
  PERMISSION = 'permission',
  UNKNOWN = 'unknown',
}

/**
 * Collects comprehensive error context including browser info, user info, and workspace context.
 *
 * @returns Error context object with user and environment information
 */
const getErrorContext = () => {
  const url = window.location.href;

  const timestamp = dayjs().toISOString();

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    return {
      name: browser,
      version: ua.match(/\d+\.\d+\.\d+/)?.[0] || 'Unknown',
      userAgent: ua,
    };
  };

  let userInfo: { userId?: string; email?: string; displayName?: string } = {};
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsedAuth = JSON.parse(authStorage);
      const user = parsedAuth?.state?.user;
      if (user) {
        userInfo = {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName,
        };
      }
    }

    // Fallback to Mixpanel distinct ID if auth store is not available
    if (
      !userInfo.userId &&
      typeof window !== 'undefined' &&
      window.mixpanel?.get_distinct_id?.()
    ) {
      userInfo.userId = window.mixpanel.get_distinct_id();
    }
  } catch {
    // Ignore parsing errors
  }

  return {
    timestamp,
    url,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    referrer: document.referrer,
    browser: getBrowserInfo(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    ...userInfo,
  };
};

/**
 * Automatically categorizes errors based on error message and context.
 *
 * @param error - The error object to categorize
 * @param context - Optional context object with additional information
 * @returns Error category
 */
const categorizeError = (
  error: unknown,
  context?: Record<string, unknown>
): ErrorCategory => {
  const message = getErrorMessage(error).toLowerCase();
  const stack = getErrorStack(error).toLowerCase();

  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('cors')
  ) {
    return ErrorCategory.NETWORK;
  }
  if (
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('401') ||
    message.includes('403')
  ) {
    return ErrorCategory.AUTHENTICATION;
  }
  if (message.includes('permission') || message.includes('access denied')) {
    return ErrorCategory.PERMISSION;
  }
  if (
    context?.endpoint ||
    message.includes('api') ||
    message.includes('400') ||
    message.includes('404') ||
    message.includes('500')
  ) {
    return ErrorCategory.API;
  }
  if (
    message.includes('validation') ||
    message.includes('invalid') ||
    message.includes('required')
  ) {
    return ErrorCategory.VALIDATION;
  }
  if (
    stack.includes('react') ||
    stack.includes('component') ||
    message.includes('render')
  ) {
    return ErrorCategory.UI;
  }
  if (error instanceof Error) {
    return ErrorCategory.RUNTIME;
  }

  return ErrorCategory.UNKNOWN;
};

/**
 * Determines error severity based on error message and category.
 *
 * @param error - The error object
 * @param category - The error category
 * @returns Error severity level
 */
const getErrorSeverity = (
  error: unknown,
  category: ErrorCategory
): ErrorSeverity => {
  const message = getErrorMessage(error).toLowerCase();

  if (
    message.includes('cannot read') ||
    message.includes('undefined is not a function') ||
    message.includes('network error') ||
    category === ErrorCategory.AUTHENTICATION
  ) {
    return ErrorSeverity.CRITICAL;
  }

  if (
    category === ErrorCategory.API ||
    category === ErrorCategory.PERMISSION ||
    message.includes('500') ||
    message.includes('error')
  ) {
    return ErrorSeverity.HIGH;
  }

  if (
    category === ErrorCategory.VALIDATION ||
    category === ErrorCategory.UI ||
    message.includes('400') ||
    message.includes('404')
  ) {
    return ErrorSeverity.MEDIUM;
  }

  return ErrorSeverity.LOW;
};

/**
 * Tracks errors with enhanced context for debugging and alerting.
 * Can be used to track errors not caught by automatic error capture.
 *
 * @param options - Error tracking options
 * @param options.message - Optional error message
 * @param options.error - The error object
 * @param options.context - Additional context for the error
 * @param options.severity - Optional severity override
 * @param options.category - Optional category override
 */
export const trackError = ({
  message,
  error,
  context,
  severity,
  category,
}: {
  message?: string;
  error?: unknown;
  context?: Record<string, unknown>;
  severity?: ErrorSeverity;
  category?: ErrorCategory;
}) => {
  const errorMessage =
    message ||
    (isErrorWithMessage(error) ? error.message : undefined) ||
    (error != null ? String(error) : 'Unknown error');
  const errorStack = getErrorStack(error) || undefined;
  const errorContext = getErrorContext();
  const autoCategory = category || categorizeError(error, context);
  const autoSeverity = severity || getErrorSeverity(error, autoCategory);

  const errorSummary = {
    title: `${autoSeverity.toUpperCase()} ${autoCategory.toUpperCase()} Error`,
    message: errorMessage,
    location: `${errorContext.pathname}${errorContext.search}`,
    user: errorContext.email
      ? `${errorContext.displayName} (${errorContext.email})`
      : 'Anonymous',
    browser: `${errorContext.browser.name} ${errorContext.browser.version}`,
    timestamp: errorContext.timestamp,
  };

  const createMixpanelDebugLink = () => {
    const projectId = config.VITE_MIXPANEL_PROJECT_ID;
    const viewId = config.VITE_MIXPANEL_VIEW_ID;
    if (!projectId || !viewId) {
      return '';
    }
    return `https://mixpanel.com/project/${projectId}/view/${viewId}/app/events`;
  };

  const mixpanelLink = createMixpanelDebugLink();

  const enhancedProperties = {
    message: errorMessage,
    stack: errorStack,
    category: autoCategory,
    severity: autoSeverity,
    error_title: errorSummary.title,
    error_summary: `${errorSummary.title}: ${errorMessage}`,
    mixpanel_debug_link: mixpanelLink,
    alert_message:
      `🚨 *${errorSummary.title}*\n` +
      `📍 *Location:* ${errorSummary.location}\n` +
      `👤 *User:* ${errorSummary.user}\n` +
      `🌐 *Browser:* ${errorSummary.browser}\n` +
      `💬 *Message:* ${errorMessage}\n` +
      `⏰ *Time:* ${dayjs(errorSummary.timestamp).format('MM/DD/YYYY, hh:mm:ss A')}\n` +
      `🔍 *Debug:* <${mixpanelLink}|View in Mixpanel>`,
    ...errorContext,
    ...context,
    raw_error: error != null ? String(error) : 'undefined',
  };

  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.track('Error', enhancedProperties);
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.track('Error', enhancedProperties);
  }

  // Log to console for debugging
  Logger.group(`🚨 ${errorSummary.title}`);
  Logger.error('Message:', errorMessage);
  Logger.error('Category:', autoCategory);
  Logger.error('Severity:', autoSeverity);
  Logger.error('Context:', context);
  Logger.error('Full Error:', error);
  Logger.groupEnd();
};

/**
 * Tracks API-related errors.
 *
 * @param error - The error object
 * @param endpoint - The API endpoint that failed
 * @param method - Optional HTTP method
 */
export const trackAPIError = (
  error: unknown,
  endpoint: string,
  method?: string
) => {
  trackError({
    error,
    category: ErrorCategory.API,
    context: { endpoint, method },
  });
};

/**
 * Tracks network-related errors (CORS, fetch failures, etc.).
 *
 * @param error - The error object
 * @param endpoint - Optional endpoint that failed
 */
export const trackNetworkError = (error: unknown, endpoint?: string) => {
  trackError({
    error,
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.CRITICAL,
    context: { endpoint },
  });
};

/**
 * Tracks authentication-related errors.
 *
 * @param error - The error object
 * @param context - Optional additional context
 */
export const trackAuthError = (
  error: unknown,
  context?: Record<string, unknown>
) => {
  trackError({
    error,
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    context,
  });
};

/**
 * Tracks validation errors.
 *
 * @param error - The error object
 * @param field - Optional field name that failed validation
 * @param value - Optional value that failed validation
 */
export const trackValidationError = (
  error: unknown,
  field?: string,
  value?: unknown
) => {
  trackError({
    error,
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    context: { field, value },
  });
};

/**
 * Tracks UI-related errors (React component errors, render failures, etc.).
 *
 * @param error - The error object
 * @param component - Optional component name where error occurred
 */
export const trackUIError = (error: unknown, component?: string) => {
  trackError({
    error,
    category: ErrorCategory.UI,
    severity: ErrorSeverity.MEDIUM,
    context: { component },
  });
};

/**
 * Identifies user in analytics services after login.
 *
 * @param user - User information
 * @param user.uid - User ID
 * @param user.email - User email
 * @param user.displayName - Optional display name
 */
export const identifyUser = (user: {
  uid: string;
  email: string;
  displayName?: string;
}) => {
  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.identify(user.uid);
    mixpanel.people.set({
      $email: user.email,
      $name: user.displayName || user.email,
      $created: dayjs().toISOString(),
      company_domain: user.email.split('@')[1], // Helps group errors by company
    });
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.setUserId(user.uid);
  }
};

/**
 * Tracks session start to ensure analytics capture user sessions properly.
 *
 * @param user - User information
 * @param user.uid - User ID
 * @param user.email - User email
 * @param user.displayName - Optional display name
 * @param isReturningSession - Whether this is a returning user session
 */
export const trackSessionStart = (
  user: {
    uid: string;
    email: string;
    displayName?: string;
  },
  isReturningSession: boolean = false
) => {
  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.identify(user.uid);

    mixpanel.people.set_once({
      $email: user.email,
      $name: user.displayName || user.email,
      company_domain: user.email.split('@')[1],
      $created: dayjs().toISOString(), // Only set on first identification
    });

    mixpanel.track('Session Started', {
      session_type: isReturningSession ? 'returning_user' : 'fresh_login',
      user_id: user.uid,
      email: user.email,
      timestamp: dayjs().toISOString(),
    });
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.setUserId(user.uid);

    amplitude.track('Session Started', {
      session_type: isReturningSession ? 'returning_user' : 'fresh_login',
      user_id: user.uid,
      email: user.email,
      timestamp: dayjs().toISOString(),
    });
  }
};

/**
 * Resets user identification in analytics services (for logout).
 */
export const resetUser = () => {
  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.reset();
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.setUserId(undefined);
  }
};

/**
 * Tracks custom events with properties.
 * Automatically attaches user and organization context if available.
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional event properties
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  let organizationId: string | undefined;
  let userEmail: string | undefined;
  let userId: string | undefined;

  try {
    const { workspace, currentWorkspaceId } =
      useWorkspaceStore.getState?.() ?? {};
    organizationId = workspace?.id ?? currentWorkspaceId;
  } catch {
    organizationId = undefined;
  }

  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsedAuth = JSON.parse(authStorage);
      const user = parsedAuth?.state?.user;
      if (user) {
        userEmail = user.email;
        userId = user.uid;
      }
    }
  } catch {
    // Ignore malformed auth storage entries
  }

  const baseProperties: Record<string, unknown> = properties
    ? { ...properties }
    : {};

  if (userEmail && baseProperties.email == null) {
    baseProperties.email = userEmail;
  }

  if (userId && baseProperties.user_id == null) {
    baseProperties.user_id = userId;
  }

  const finalProperties =
    organizationId && baseProperties.organization_id == null
      ? { ...baseProperties, organization_id: organizationId }
      : baseProperties;

  if (config.VITE_MIXPANEL_TOKEN) {
    mixpanel.track(eventName, finalProperties);
  }

  if (config.VITE_AMPLITUDE_API_KEY) {
    amplitude.track(eventName, finalProperties);
  }
};

/**
 * Test function for development to verify error tracking.
 * Only available in development environment.
 */
export const testErrorTracking = () => {
  if (import.meta.env.DEV) {
    trackError({
      error: new Error('Test runtime error'),
      message: 'This is a test error to verify enhanced tracking',
      context: { testType: 'runtime', location: 'analytics test' },
    });

    trackAPIError(new Error('Test API error'), '/api/test', 'GET');

    trackAuthError(new Error('Test auth error'), { provider: 'firebase' });
  }
};

if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.testErrorTracking = testErrorTracking;
  window.trackError = trackError;
  window.trackAPIError = trackAPIError;
}
